import {
  CustomerBillingPayload,
  CustomerManualTransactionPayload,
  CustomerTransactionPayload,
  Notification,
  NotificationEvents,
  ScannedTransactionPayload,
} from "@lib/types/Notification";
import { formatCurrency } from "./StringHelper";
import { getRoute } from "@lib/helpers/RouteHelper";
import { ROUTES } from "@lib/constants";
import {
  AuthRoles,
  TransactionProxyState,
  TransactionProxyStatus,
} from "@lib/types";
import { MessageFormatElement } from "react-intl";

// html tags
type TagType = "b" | "small";
type Tags = Record<string, TagType>;
type I18nValues = Record<string, string | number | null>;
type NotificationProps = {
  url: string;
  title: {
    id: string;
    values?: I18nValues;
    tags?: Tags[];
  };
  description: {
    id: string;
    values?: I18nValues;
    tags?: Tags[];
  };
  clearKeys?: string[];
};

type GetNotificationProps = {
  notification: Notification;
  role: AuthRoles;
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
};

export const getNotificationProps = (
  props: GetNotificationProps,
): NotificationProps => {
  const { notification, role, messages } = props;
  const event = notification?.event as NotificationEvents;

  const prepare = () => {
    switch (event) {
      case NotificationEvents.CustomerPayBillRequest:
        return getCustomerPayBillRequestProps(notification);

      case NotificationEvents.CustomerManualTransactionRequest:
        return getCustomerManualTransactionRequestProps(notification);

      case NotificationEvents.ScannedTransactionFound:
        return getScannedTransactionFoundProps(notification);

      case NotificationEvents.CustomerTransactionRequest:
        return getCustomerTransactionRequestProps(props);

      default:
        return {
          url: "/",
          title: { id: "common.notification" },
          description: { id: `${event as any}` },
        };
    }
  };

  const result = prepare();

  // Policy based on role
  if (role === AuthRoles.CustomerAgent) {
    if (result?.description?.values?.providerName) {
      result.description.values.providerName = messages[
        "common.provider"
      ] as string;
    }
  }
  if (role === AuthRoles.ProviderAgent) {
    if (result?.description?.values?.customerName) {
      result.description.values.customerName = messages[
        "common.customer"
      ] as string;
    }
  }

  return result;
};

const getCustomerPayBillRequestProps = (
  notification: Notification,
): NotificationProps => {
  const payload = notification?.payload as CustomerBillingPayload;
  const customer = { ...payload?.customer, ...payload?.wallet?.customer };
  return {
    url: getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL_TAB, {
      customerId: customer?.id,
      tab: "blockchainPayments",
    }),
    title: {
      id: `notification.name.${notification?.event}`,
    },
    description: {
      id: `notification.events.${notification?.event}`,
      values: {
        customerName: customer?.name,
      },
    },
  };
};

const getCustomerManualTransactionRequestProps = (
  notification: Notification,
): NotificationProps => {
  const payload = notification?.payload as CustomerManualTransactionPayload;
  const customer = { ...payload?.customer, ...payload?.transaction?.customer };
  const currency = payload?.transaction?.currency;
  const amount = formatCurrency(payload?.transaction?.amount, currency as any);

  return {
    url: getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL_TAB, {
      customerId: customer?.id,
      tab: "cashPaymentHistory",
    }),
    title: {
      id: `notification.name.${notification?.event}`,
    },
    description: {
      id: `notification.events.${notification?.event}`,
      values: {
        customerName: customer?.name,
        amount: amount,
        currency: currency,
      },
    },
  };
};

const getScannedTransactionFoundProps = (
  notification: Notification,
): NotificationProps => {
  const payload = notification?.payload as ScannedTransactionPayload;
  const customer = { ...payload?.customer, ...payload?.wallet?.customer };
  const customerUser = {
    ...payload?.wallet?.customerUser,
  };
  const currency = payload?.wallet?.network;
  const amount = formatCurrency(payload?.amount, currency as any);

  if (customerUser?.id) {
    return {
      url: getRoute(ROUTES.ADMIN.CUSTOMER_LEAD.DETAIL_TAB, {
        customerId: customer?.id,
        tab: "blockchainPaymentRequests",
      }),
      title: {
        id: `notification.name.${notification?.event}.customerLead`,
      },
      description: {
        id: `notification.events.${notification?.event}.customerLead`,
        values: {
          customerLeadName:
            customerUser?.firstName + " " + customerUser?.lastName,
          amount: amount,
          currency: currency,
        },
      },
    };
  }

  return {
    url: getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL_TAB, {
      customerId: customer?.id,
      tab: "transactionHistory",
    }),
    title: {
      id: `notification.name.${notification?.event}`,
    },
    description: !!payload.amount
      ? {
          id: `notification.events.${notification?.event}`,
          values: {
            customerName: customer?.name,
            amount: amount,
            currency: currency,
          },
          tags: [
            {
              amount: "b",
              currency: "small",
            },
          ],
        }
      : {
          id: `notification.events.${notification.event}.no-amount`,
          values: {
            customerName: customer?.name,
          },
        },
  };
};

const getCustomerTransactionRequestProps = (
  props: GetNotificationProps,
): NotificationProps => {
  const { notification } = props;
  const payload = notification?.payload as CustomerTransactionPayload;
  const transaction = payload?.transaction;
  const customer = { ...payload?.customer, ...transaction?.customer };
  const provider = { ...payload?.provider, ...transaction?.provider };
  const providerName = provider?.name || " N/A ";
  const customerName = customer?.name || " N/A ";
  const currency =
    transaction?.currency || transaction?.providerTransactionConfig?.currency;
  const amount = formatCurrency(transaction?.amount, currency as any);
  const status = transaction?.status;
  const state = transaction?.state;

  const url = getRoute(ROUTES.ADMIN.PAYMENT_PROVIDER.PAYMENT_HISTORY.LIST);

  const description = {
    id: `notification.events.${notification?.event}`,
    values: {
      customerName: customerName,
      providerName: providerName,
      amount: amount,
      currency: currency,
      status: status,
      state: state,
    },
  };

  if (status === TransactionProxyStatus.Success) {
    description.id = `notification.events.${notification?.event}.initialized`;
  }
  if (status === TransactionProxyStatus.Pending) {
    description.id = `notification.events.${notification?.event}.pending`;
    if (state === TransactionProxyState.PendingProviderApproval) {
      description.id = `notification.events.${notification?.event}.pending.providerApproval`;
    }
    if (state === TransactionProxyState.PendingForAccountInfo) {
      description.id = `notification.events.${notification?.event}.pending.accountInfo`;
    }
    if (state === TransactionProxyState.PendingCustomerApproval) {
      description.id = `notification.events.${notification?.event}.pending.customerApproval`;
    }
  }
  if (status === TransactionProxyStatus.Failed) {
    description.id = `notification.events.${notification?.event}.failed`;
  }

  return {
    url: url,
    title: {
      id: `notification.name.${notification?.event}`,
    },
    description: description,
    clearKeys: [
      "transaction",
      "transactions",
      "payment-history-transaction",
      "customer-dashboard-transactions",
    ],
  };
};
